import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export enum Identifier {
  TYPE_API_KEY = 'apiKey',
  TYPE_TOKEN = 'token',
  TYPE_MEMBER_TOKEN = 'memberToken',
  TYPE_USER = 'user',
  TYPE_CLINIC = 'clinic',
  TYPE_FRANCHISE_CLINICS = 'franchiseClinics',
  TYPE_CLINIC_GROUP_ID = 'clinicGroupId',
  TYPE_LANGUAGE = 'culture',
  TYPE_ChatToken = 'authJwtToken',
  TYPE_PORTAL_SETUP = 'portalSetup',
  TYPE_FRANCHISE_ID = 'clinicGroupFranchiseId',
  TYPE_FRANCHISE_NAME = 'clinicGroupFranchiseName',
  TYPE_SSO_INFO = 'ssoInfo',
  TYPE_NAV_BAR_OPEN = 'navBarState',
  TYPE_APPOINTMENT_BOOKING_MODE = 'appointmentBookingMode',
  TYPE_IS_NARROW_AUTHENTICATION_DURATION_MODE = 'isNarrowAuthenticationDurationMode',
  TYPE_SOURCE_SESSION_TOKEN = 'sourceSessionToken',
  TYPE_FRANCHISE_LANGUAGE_IDS = 'clinicFranchiseSupportedLanguageIds',
  TYPE_TERMS_CONDITIONS = 'termsAndConditions',
  TYPE_CLINIC_MODULES = 'clinicModules',
  TYPE_FRANCHISE_MODULES = 'franchiseModules',
  TYPE_COOKIE_TERMS_ACCEPTED = 'cookieTermsAccepted',
}

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService implements OnDestroy {
  private readonly subscription: Subscription = new Subscription();
  private storageSubjectsMap: Map<string, BehaviorSubject<string | null>>;
  private storageValues: { [key: string]: string | null } = {};

  constructor() {
    this.storageSubjectsMap = new Map<string, BehaviorSubject<string | null>>();
    this.checkLocalStorageChanges();
  }

  checkLocalStorageChanges(): void {
    const checkLocalStoreChangesTimer$ = timer(10000, 5000);
    this.subscription.add(
      checkLocalStoreChangesTimer$.subscribe(() => {
        for (const [key, value] of this.storageSubjectsMap.entries()) {
          const storageItem = localStorage.getItem(key);
          if (this.storageValues[key] !== storageItem) {
            this.storageValues[key] = storageItem;
            value.next(storageItem);
          }
        }
      })
    );
  }

  public getStorageItem(identifier: Identifier): BehaviorSubject<string | null> {
    const storageSubject = this.getStorageSubject(identifier);
    const storageItem = localStorage.getItem(identifier);
    if (this.storageValues[identifier] !== storageItem) {
      this.storageValues[identifier] = storageItem;
      storageSubject.next(storageItem);
    }
    return storageSubject;
  }

  public getStorageModel<T>(identifier: Identifier): Observable<T> {
    return this.getStorageItem(identifier).pipe(
      filter((item): item is string => !!item),
      map((item: string) => JSON.parse(item))
    );
  }

  public setStorageItem(identifier: Identifier, object: string): void {
    this.storageValues[identifier] = object;
    localStorage.setItem(identifier, object);
    this.getStorageSubject(identifier).next(object);
  }

  public removeStorageItem(identifier: Identifier): void {
    localStorage.removeItem(identifier);
    this.getStorageSubject(identifier).next(null);
  }

  public clear(): void {
    localStorage.clear();
    this.storageSubjectsMap.forEach((storageSubject: BehaviorSubject<string | null>) => {
      storageSubject.next(null);
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private getStorageSubject(identifier: Identifier): BehaviorSubject<string | null> {
    let storageSubject = this.storageSubjectsMap.get(identifier);
    if (!storageSubject) {
      storageSubject = new BehaviorSubject<string | null>(null);
      this.storageSubjectsMap.set(identifier, storageSubject);
    }
    return storageSubject;
  }
}
